// Single source of truth for the site palette.
// Light is the default; the mode is persisted in localStorage under "themeMode".
import { createTheme, Theme } from "@mui/material/styles";

export type ThemeMode = "light" | "dark";

export const THEME_STORAGE_KEY = "themeMode";

// Tokens are mirrored onto CSS custom properties so plain sx strings
// (and the SCSS file) can reference them without needing theme access.
type Tokens = Record<string, string>;

// Controls layered directly on top of album art. Artwork is arbitrary in both
// modes, so these stay fixed rather than following the light/dark palette.
const MEDIA_TOKENS: Tokens = {
  "on-media": "#ffffff",
  "on-media-bg": "rgba(0,0,0,0.55)",
  "on-media-bg-hover": "rgba(0,0,0,0.75)",
  "media-scrim": "rgba(0,0,0,0.55)",
};

type Palette = {
  bg: string;
  s1: string;
  s2: string;
  s3: string;
  text: string;
  text2: string;
  faint: string;
  borderRgb: string;
  accent: string;
  accentHover: string;
  accentSoft: string;
  onAccent: string;
};

const build = (mode: ThemeMode, p: Palette): Tokens => {
  const overlayRgb = mode === "light" ? p.borderRgb : "255,255,255";
  return {
    "page-bg": p.bg,
    "surface-1": p.s1,
    "surface-2": p.s2,
    "surface-3": p.s3,
    "border-soft": `rgba(${p.borderRgb},${mode === "light" ? 0.12 : 0.1})`,
    "border-mid": `rgba(${p.borderRgb},${mode === "light" ? 0.22 : 0.18})`,
    "border-strong": `rgba(${p.borderRgb},${mode === "light" ? 0.38 : 0.32})`,
    "text-primary": p.text,
    "text-secondary": p.text2,
    "text-faint": p.faint,
    "hover-overlay": `rgba(${overlayRgb},${mode === "light" ? 0.05 : 0.06})`,
    "hover-overlay-strong": `rgba(${overlayRgb},${mode === "light" ? 0.1 : 0.12})`,
    accent: p.accent,
    "accent-hover": p.accentHover,
    "accent-soft": p.accentSoft,
    "on-accent": p.onAccent,
    scrim: mode === "light" ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.65)",
    "card-shadow": mode === "light" ? "0 1px 3px rgba(0,0,0,0.10)" : "0 1px 3px rgba(0,0,0,0.35)",
    "card-shadow-strong":
      mode === "light" ? "0 8px 24px rgba(0,0,0,0.14)" : "0 8px 24px rgba(0,0,0,0.45)",
  };
};

// "Slate" — mid-tone grey. The page background carries the tint and cards sit
// a step lighter on top of it, so the layout has depth without being white.
const LIGHT_TOKENS: Tokens = build("light", {
  bg: "#dee2e6", s1: "#eef1f3", s2: "#d2d8dd", s3: "#c4ccd2",
  text: "#171c20", text2: "#434c53", faint: "#68727a", borderRgb: "23,28,32",
  accent: "#37596e", accentHover: "#2b4757", accentSoft: "rgba(55,89,110,0.12)", onAccent: "#ffffff",
});

// "Deep harbor" — navy-leaning charcoal.
const DARK_TOKENS: Tokens = build("dark", {
  bg: "#131820", s1: "#1c2430", s2: "#24303e", s3: "#0e131a",
  text: "#e4eaf2", text2: "#a5b2c2", faint: "#78859a", borderRgb: "255,255,255",
  accent: "#7fa8ca", accentHover: "#97bcda", accentSoft: "rgba(127,168,202,0.16)", onAccent: "#0d1720",
});

export const TOKENS: Record<ThemeMode, Tokens> = {
  light: { ...LIGHT_TOKENS, ...MEDIA_TOKENS },
  dark: { ...DARK_TOKENS, ...MEDIA_TOKENS },
};

export function tokensFor(mode: ThemeMode): Tokens {
  return TOKENS[mode];
}

// Mirrored in public/index.html to paint the background before React mounts.
export const PAGE_BG: Record<ThemeMode, string> = {
  light: "#dee2e6",
  dark: "#131820",
};

// Tier block colours. Shared by the editable tier list and the read-only
// share view so both render an existing list identically.
export const TIER_COLORS: Record<string, string> = {
  S: "#e0b73f",
  A: "#5cb87a",
  B: "#4f95cf",
  C: "#9a7fc4",
  D: "#e08c3f",
  E: "#d45b5b",
  F: "#966b4e",
};

export const DEFAULT_TIER_COLORS = ["#e0b73f", "#5cb87a", "#4f95cf", "#9a7fc4", "#e08c3f", "#d45b5b", "#966b4e"];

export const FALLBACK_TIER_COLOR = "#9b948b";

// Tier labels always sit on a saturated block, so they keep a fixed dark ink.
export const TIER_LABEL_INK = "#221f1a";

export function getStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyThemeTokens(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  Object.entries(TOKENS[mode]).forEach(([name, value]) => {
    root.style.setProperty(`--${name}`, value);
  });
  root.setAttribute("data-theme", mode);
  root.style.colorScheme = mode;
  // The page background is the one value that must also live on the element
  // itself, so the area outside the app root matches.
  root.style.backgroundColor = PAGE_BG[mode];
}

// Module-level mirror of the active mode. The provider keeps this in sync so
// non-React callers (basicFunctions.getTheme) build the matching MUI theme.
let currentMode: ThemeMode = "light";

export function setCurrentMode(mode: ThemeMode) {
  currentMode = mode;
}

export function getCurrentMode(): ThemeMode {
  return currentMode;
}

const cache: Partial<Record<ThemeMode, Theme>> = {};

export function buildTheme(mode: ThemeMode): Theme {
  const cached = cache[mode];
  if (cached) return cached;

  const t = TOKENS[mode];
  const theme = createTheme({
    palette: {
      mode,
      primary: { main: t.accent, dark: t["accent-hover"], contrastText: t["on-accent"] },
      secondary: { main: t["accent-hover"] },
      background: { default: t["page-bg"], paper: t["surface-1"] },
      text: { primary: t["text-primary"], secondary: t["text-secondary"], disabled: t["text-faint"] },
      divider: t["border-soft"],
      // action.selected feeds MUI's default Chip fill and selected rows, so it
      // stays a neutral overlay — tying it to the accent made plain chips read
      // as accent-coloured buttons.
      action: {
        active: t["text-secondary"],
        selected: t["hover-overlay-strong"],
        hover: t["hover-overlay"],
      },
      success: { main: mode === "light" ? "#2e7d4f" : "#6bbf8a" },
      error: { main: mode === "light" ? "#c0392b" : "#e07a6f" },
      warning: { main: mode === "light" ? "#b07d17" : "#e0b73f" },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700, color: t["text-primary"] },
      body1: { color: t["text-primary"] },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: "none", backgroundImage: "none" },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: "20px", padding: "8px 20px", textTransform: "none" },
          contained: {
            backgroundColor: t.accent,
            color: t["on-accent"],
            boxShadow: "none",
            "&:hover": { backgroundColor: t["accent-hover"], boxShadow: "none" },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: t["surface-1"],
            backgroundImage: "none",
            boxShadow: t["card-shadow"],
            borderRadius: "8px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
    },
  });

  cache[mode] = theme;
  return theme;
}
